/**
 * Who can do what.
 *
 * Authorisation lives in this database, never in the identity provider. That is a
 * direct lesson from the legacy system: with `resetOnLogin: true` the SAML provider
 * rebuilt roles from Google's group claim on every sign-in and dropped anyone
 * outside a mapped group to a plain user. Google tells us WHO someone is; this
 * table decides what they may do.
 */
export const ROLES = ['EMPLOYEE', 'SUPERVISOR', 'HR', 'ADMIN'] as const;

export type Role = (typeof ROLES)[number];

/**
 * Ordered, and the order is load-bearing: every check is "at least this role".
 * A flat set would need each call site to list the roles that count, which is how
 * one of them ends up forgetting ADMIN.
 */
const RANK: Record<Role, number> = {
  EMPLOYEE: 0,
  SUPERVISOR: 1,
  HR: 2,
  ADMIN: 3,
};

export function atLeast(role: Role, required: Role): boolean {
  return RANK[role] >= RANK[required];
}

export function isRole(value: unknown): value is Role {
  return typeof value === 'string' && (ROLES as readonly string[]).includes(value);
}

/**
 * Who may see money, and who may change it.
 *
 * Separate from the role ladder because seeing an hour and seeing what that hour
 * bills are different permissions, and the previous system kept them apart too:
 * ROLE_TEAMLEAD there held `view_rate_own_timesheet` and
 * `view_rate_other_timesheet` but not the edit pair, while ROLE_ADMIN and above
 * held both. A supervisor who can approve a week can therefore see what it is
 * worth and cannot alter it - which is the right way round, because the person
 * checking a figure should not be the person able to change it.
 *
 * An employee sees neither. They can see the hours they worked, which is theirs;
 * what the company charges for them is not, and leaking it through a timesheet
 * screen is how a rate card reaches a client.
 */
export function canViewRates(role: Role): boolean {
  return atLeast(role, 'SUPERVISOR');
}

export function canEditRates(role: Role): boolean {
  return atLeast(role, 'HR');
}

/**
 * Who may say outright whether an entry is billable. Everyone else gets the answer
 * the customer, project and activity give (defaultBillable), as in the previous
 * system, where the BILLABLE permissions started at team lead. Utilisation sums
 * this flag, so it is not the person being measured's to set.
 */
export function canEditBillable(role: Role): boolean {
  return atLeast(role, 'SUPERVISOR');
}
